import * as XLSX from 'xlsx';

/**
 * Column configuration for Excel export
 */
export interface ExcelColumnConfig<T> {
  /** The key in the data object */
  key: keyof T | string;
  /** Display header in Excel */
  header: string;
  /** Optional width (in characters) */
  width?: number;
  /** Optional value transformer */
  transform?: (value: any, row: T) => string | number | boolean | Date | null;
}

/**
 * Options for Excel export
 */
export interface ExcelExportOptions<T> {
  /** Data array to export */
  data: T[];
  /** Column configuration (if not provided, all keys from first row are used) */
  columns?: ExcelColumnConfig<T>[];
  /** Sheet name (default: "Sheet1") */
  sheetName?: string;
  /** File name without extension (default: "export") */
  fileName?: string;
  /** Include timestamp in filename */
  includeTimestamp?: boolean;
}

/**
 * Options for multi-sheet Excel export
 */
export interface MultiSheetExcelOptions {
  /** Array of sheet configurations */
  sheets: Array<{
    name: string;
    data: any[];
    columns?: ExcelColumnConfig<any>[];
  }>;
  /** File name without extension */
  fileName?: string;
  /** Include timestamp in filename */
  includeTimestamp?: boolean;
}

/**
 * Transforms data row based on column config
 */
function transformRow<T>(row: T, columns: ExcelColumnConfig<T>[]): Record<string, any> {
  const result: Record<string, any> = {};
  for (const col of columns) {
    const key = col.key as string;
    const rawValue = (row as any)[key];
    result[col.header] = col.transform ? col.transform(rawValue, row) : rawValue;
  }
  return result;
}

/**
 * Auto-generates columns from first data row
 */
function autoGenerateColumns<T>(data: T[]): ExcelColumnConfig<T>[] {
  if (data.length === 0) return [];
  const firstRow = data[0];
  return Object.keys(firstRow as object).map((key) => ({
    key: key as keyof T,
    header: formatHeader(key),
  }));
}

/**
 * Formats a camelCase or snake_case key into a readable header
 */
function formatHeader(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Calculates optimal column widths
 */
function calculateColumnWidths<T>(
  data: Record<string, any>[],
  columns: ExcelColumnConfig<T>[]
): { wch: number }[] {
  return columns.map((col) => {
    if (col.width) return { wch: col.width };
    
    const headerLength = col.header.length;
    const maxDataLength = data.reduce((max, row) => {
      const val = row[col.header];
      const len = val != null ? String(val).length : 0;
      return Math.max(max, len);
    }, 0);
    
    return { wch: Math.min(Math.max(headerLength, maxDataLength) + 2, 50) };
  });
}

/**
 * Creates a worksheet from data and columns
 */
function createWorksheet<T>(
  data: T[],
  columns?: ExcelColumnConfig<T>[]
): XLSX.WorkSheet {
  const cols = columns || autoGenerateColumns(data);
  const transformedData = data.map((row) => transformRow(row, cols));
  
  const worksheet = XLSX.utils.json_to_sheet(transformedData);
  worksheet['!cols'] = calculateColumnWidths(transformedData, cols);
  
  return worksheet;
}

/**
 * Generates filename with optional timestamp
 */
function generateFileName(baseName: string, includeTimestamp?: boolean): string {
  if (!includeTimestamp) return `${baseName}.xlsx`;
  
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
  return `${baseName}_${timestamp}.xlsx`;
}

/**
 * Exports data to an Excel file (single sheet)
 * 
 * @example
 * // Basic usage - auto-generates columns from data keys
 * exportToExcel({
 *   data: transactions,
 *   fileName: 'transactions'
 * });
 * 
 * @example
 * // With custom columns and transformers
 * exportToExcel({
 *   data: transactions,
 *   fileName: 'transactions',
 *   columns: [
 *     { key: 'id', header: 'Transaction ID' },
 *     { key: 'dateTime', header: 'Date', transform: (v) => new Date(v).toLocaleDateString() },
 *     { key: 'amount', header: 'Amount', transform: (v) => `$${v.toFixed(2)}` },
 *     { key: 'action', header: 'Action', transform: (v) => v?.transaction_entity_name || '' },
 *   ],
 *   includeTimestamp: true
 * });
 */
export function exportToExcel<T extends object>(options: ExcelExportOptions<T>): void {
  const {
    data,
    columns,
    sheetName = 'Sheet1',
    fileName = 'export',
    includeTimestamp = false,
  } = options;

  if (data.length === 0) {
    console.warn('exportToExcel: No data to export');
    return;
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = createWorksheet(data, columns);
  
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  const finalFileName = generateFileName(fileName, includeTimestamp);
  XLSX.writeFile(workbook, finalFileName);
}

/**
 * Exports data to an Excel file with multiple sheets
 * 
 * @example
 * exportToExcelMultiSheet({
 *   sheets: [
 *     { name: 'Transactions', data: transactions, columns: transactionColumns },
 *     { name: 'Summary', data: summaryData },
 *   ],
 *   fileName: 'financial-report',
 *   includeTimestamp: true
 * });
 */
export function exportToExcelMultiSheet(options: MultiSheetExcelOptions): void {
  const {
    sheets,
    fileName = 'export',
    includeTimestamp = false,
  } = options;

  if (sheets.length === 0) {
    console.warn('exportToExcelMultiSheet: No sheets to export');
    return;
  }

  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    if (sheet.data.length === 0) continue;
    const worksheet = createWorksheet(sheet.data, sheet.columns);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
  }

  const finalFileName = generateFileName(fileName, includeTimestamp);
  XLSX.writeFile(workbook, finalFileName);
}

/**
 * Converts data to CSV string (for clipboard or other uses)
 */
export function dataToCSV<T extends object>(
  data: T[],
  columns?: ExcelColumnConfig<T>[]
): string {
  if (data.length === 0) return '';

  const cols = columns || autoGenerateColumns(data);
  const headers = cols.map((c) => c.header).join(',');
  
  const rows = data.map((row) => {
    const transformed = transformRow(row, cols);
    return cols
      .map((col) => {
        const val = transformed[col.header];
        if (val == null) return '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      })
      .join(',');
  });

  return [headers, ...rows].join('\n');
}

/**
 * Downloads CSV data as a file
 */
export function downloadCSV(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}
